import type { Entity } from '@backstage/catalog-model';
import type { OAuthApi } from '@backstage/core-plugin-api';
import type {
  CicdState,
  FetchBuildsOptions,
} from '@backstage-community/plugin-cicd-statistics';
import { CicdStatisticsApiGitlab } from '@backstage-community/plugin-cicd-statistics-module-gitlab';
import { GitlabAuthRequiredError } from '../gitlab-pipelines/gitlabPipelinesAuth';

const GITLAB_TOKEN_SCOPES = ['read_user', 'api'];

export function optionalGitlabAuthApi(gitlabAuthApi: OAuthApi): OAuthApi {
  return new Proxy(gitlabAuthApi, {
    get(target, prop, receiver) {
      if (prop === 'getAccessToken') {
        return async (_scopes?: string | string[]) => {
          const token = await target.getAccessToken(GITLAB_TOKEN_SCOPES, {
            optional: true,
          });
          if (!token) {
            throw new GitlabAuthRequiredError();
          }
          return token;
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  });
}

export function isUnusableCicdSourceError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return /invalid url/i.test(error.message);
  }
  if (error instanceof Error) {
    return /source[- ]location|invalid url/i.test(error.message);
  }
  return false;
}

export class PortalCicdStatisticsApiGitlab extends CicdStatisticsApiGitlab {
  constructor(gitlabAuthApi: OAuthApi) {
    super(optionalGitlabAuthApi(gitlabAuthApi));
  }

  async createGitlabApi(entity: Entity, _scopes: string[]) {
    return super.createGitlabApi(entity, GITLAB_TOKEN_SCOPES);
  }

  async fetchBuilds(options: FetchBuildsOptions): Promise<CicdState> {
    try {
      return await super.fetchBuilds(options);
    } catch (error) {
      if (isUnusableCicdSourceError(error)) {
        return { builds: [] };
      }
      throw error;
    }
  }
}
